import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Info } from '@phosphor-icons/react';

export default function DisclaimerPage() {
  const { language } = useLanguage();

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    en: {
      title: 'A few things to know',
      subtitle: 'We want to be upfront about what PaperPal is — and what it isn\'t.',
      intro: 'PaperPal is here to help you navigate the paperwork of settling in Canada. We explain processes, suggest next steps, and help you stay organized. That said, here are some important things to keep in mind:',
      sections: [
        {
          title: 'We\'re guides, not experts',
          content: 'We provide general guidance to help you understand administrative processes in Canada. We\'re not lawyers, immigration consultants, or tax professionals — and we don\'t pretend to be.'
        },
        {
          title: 'This isn\'t legal advice',
          content: 'The information we share is educational. It\'s meant to point you in the right direction, but it shouldn\'t replace advice from a qualified professional when you need it.'
        },
        {
          title: 'Every situation is different',
          content: 'Our guidance is based on common scenarios, but your situation might be unique. Rules also change over time. When in doubt, double-check with official sources.'
        },
        {
          title: 'You\'re in control',
          content: 'Ultimately, you\'re responsible for the decisions you make. We\'re here to help you feel more confident, but please verify important information before acting on it.'
        },
        {
          title: 'We\'re independent',
          content: 'PaperPal isn\'t affiliated with any government agency. We\'re just a small team trying to make newcomers\' lives a little easier.'
        }
      ],
      closing: 'Questions? We\'re happy to help.',
      contact: 'Get in touch'
    },
    fr: {
      title: 'Quelques points à savoir',
      subtitle: 'On veut être clairs sur ce que PaperPal est — et ce qu\'il n\'est pas.',
      intro: 'PaperPal est là pour vous aider à naviguer dans les démarches administratives au Canada. On vous explique les processus, on suggère les prochaines étapes et on vous aide à rester organisé. Cela dit, voici quelques points importants :',
      sections: [
        {
          title: 'On est des guides, pas des experts',
          content: 'On fournit des conseils généraux pour vous aider à comprendre les processus administratifs au Canada. On n\'est pas avocats, consultants en immigration ou fiscalistes — et on ne prétend pas l\'être.'
        },
        {
          title: 'Ce ne sont pas des conseils juridiques',
          content: 'Les informations qu\'on partage sont éducatives. Elles sont là pour vous orienter, mais ne devraient pas remplacer les conseils d\'un professionnel qualifié quand vous en avez besoin.'
        },
        {
          title: 'Chaque situation est différente',
          content: 'Nos conseils sont basés sur des scénarios courants, mais votre situation peut être unique. Les règles changent aussi avec le temps. En cas de doute, vérifiez auprès des sources officielles.'
        },
        {
          title: 'Vous êtes aux commandes',
          content: 'Au final, vous êtes responsable des décisions que vous prenez. On est là pour vous aider à vous sentir plus confiant, mais vérifiez les informations importantes avant d\'agir.'
        },
        {
          title: 'On est indépendants',
          content: 'PaperPal n\'est affilié à aucune agence gouvernementale. On est juste une petite équipe qui essaie de faciliter la vie des nouveaux arrivants.'
        }
      ],
      closing: 'Des questions? On est là pour aider.',
      contact: 'Nous contacter'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-pp-bg flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header - Friendly, not scary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 md:mb-12"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-pp-navy-light flex items-center justify-center mx-auto mb-4 md:mb-5">
              <Info size={24} className="text-pp-navy" weight="duotone" />
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-pp-text mb-3" data-testid="disclaimer-title">
              {t.title}
            </h1>
            <p className="text-pp-text-secondary text-base md:text-lg max-w-md mx-auto">
              {t.subtitle}
            </p>
          </motion.div>

          {/* Content - Clean cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Intro */}
            <p className="text-pp-text-secondary leading-relaxed text-base md:text-lg">
              {t.intro}
            </p>

            {/* Sections as simple list */}
            <div className="space-y-6 md:space-y-8">
              {t.sections.map((section, index) => (
                <div key={index} className="border-l-2 border-pp-border pl-4 md:pl-6">
                  <h2 className="font-heading font-medium text-pp-text text-base md:text-lg mb-2">
                    {section.title}
                  </h2>
                  <p className="text-pp-text-secondary text-sm md:text-base leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>

            {/* Closing */}
            <div className="pt-6 md:pt-8 border-t border-pp-border">
              <p className="text-pp-text-secondary mb-4">{t.closing}</p>
              <Link 
                to="/contact" 
                className="inline-flex items-center text-pp-navy font-medium hover:underline"
              >
                {t.contact} →
              </Link>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
